import { useEffect, useRef, useState } from "react";
import { ArrowUp, GitBranch } from "lucide-react";
import { DASHBOARD_SIGNIN_URL, DASHBOARD_SIGNUP_URL } from "@/lib/cta";

/* ─────────────────────────────────────────────────────────────
   The composer card: the prompt you would hand an agent, sitting
   in a card that looks like the composer inside the panel. Shared
   by the home hero and the agents page hero — it only reads right
   on the warm `.lv-home` surface.
   ───────────────────────────────────────────────────────────── */

/** The four prompts the hero composer types through, one after another. */
const PROMPTS = [
  "برام یه سایت بساز و با گردو آنلاین‌ش کن",
  "نسخه‌ی جدید سایت رو دامنه نمیاد، ببین مشکل چیه",
  "لاگ‌های خطای دیشب سرویس api را بررسی کن",
  "نسخه فعلی را به نسخه قبلی برگردان",
];

const TYPE_MS = 45;
const ERASE_MS = 40;
/** Hold a finished prompt long enough to read it before erasing. */
const HOLD_MS = 5000;
/** Short beat on the empty box before the next prompt starts typing. */
const BEAT_MS = 450;

/**
 * One skin per prompt: the composer wears the colours of the agent that would
 * have been asked. Index-matched to PROMPTS.
 */
export const AGENT_SKINS = [
  {
    label: "Claude",
    src: "/agents/claude.png",
    shell: "#f0eee6",
    surface: "#ffffff",
    border: "rgb(217 119 87 / 0.22)",
    text: "#3d3929",
    muted: "#83827d",
    chipRing: "rgb(217 119 87 / 0.22)",
    accent: "#d97757",
    onAccent: "#ffffff",
  },
  {
    label: "ChatGPT",
    src: "/agents/chatgpt.webp",
    shell: "#f4f4f4",
    surface: "#ffffff",
    border: "rgb(0 0 0 / 0.08)",
    text: "#0d0d0d",
    muted: "#8f8f8f",
    chipRing: "rgb(0 0 0 / 0.08)",
    accent: "#0d0d0d",
    onAccent: "#ffffff",
  },
  {
    label: "Cursor",
    src: "/agents/cursor.png",
    shell: "#0f0f0f",
    surface: "#1a1a1a",
    border: "rgb(255 255 255 / 0.10)",
    text: "#e9e9e9",
    muted: "#8c8c8c",
    chipRing: "rgb(255 255 255 / 0.12)",
    accent: "#ffffff",
    onAccent: "#0f0f0f",
    // The Cursor mark is solid black — it needs inverting on a dark surface.
    invertMark: true,
  },
  {
    label: "Antigravity",
    src: "/agents/antigravity.png",
    shell: "#eef3fd",
    surface: "#ffffff",
    border: "rgb(66 133 244 / 0.20)",
    text: "#1f1f1f",
    muted: "#80868b",
    chipRing: "rgb(66 133 244 / 0.20)",
    accent: "#4285f4",
    onAccent: "#ffffff",
  },
];

export type AgentSkin = (typeof AGENT_SKINS)[number];

/**
 * The hero object: the prompt you would hand an agent, sitting in a card that
 * looks like the composer inside the panel.
 */
function useTypedPrompt() {
  // SSG + first paint show the first prompt in full; the loop starts after
  // hydration, and reduced-motion users keep the static text.
  const [text, setText] = useState(PROMPTS[0]);
  // Which prompt is on screen — the card wears that agent's skin.
  const [active, setActive] = useState(0);

  useEffect(() => {
    if (window.matchMedia("(prefers-reduced-motion: reduce)").matches) return;

    let index = 0;
    let chars = PROMPTS[0].length;
    let erasing = false;
    let timer = 0;

    const tick = () => {
      const full = PROMPTS[index];

      if (!erasing) {
        if (chars < full.length) {
          chars += 1;
          setText(full.slice(0, chars));
          // Pause on the last character so the finished prompt is readable.
          timer = window.setTimeout(tick, chars === full.length ? HOLD_MS : TYPE_MS);
          return;
        }
        erasing = true; // hold is over
      }

      if (chars > 0) {
        chars -= 1;
        setText(full.slice(0, chars));
        timer = window.setTimeout(tick, chars === 0 ? BEAT_MS : ERASE_MS);
        return;
      }

      erasing = false;
      index = (index + 1) % PROMPTS.length;
      setActive(index);
      setText("");
      timer = window.setTimeout(tick, TYPE_MS);
    };

    timer = window.setTimeout(tick, HOLD_MS);
    return () => window.clearTimeout(timer);
  }, []);

  return { text, skin: AGENT_SKINS[active % AGENT_SKINS.length] };
}

/**
 * The composer's send button. There is nothing to submit on a marketing page,
 * so it invites the visitor to sign in or sign up instead.
 */
function SendWithAuthPopover({ skin }: { skin: AgentSkin }) {
  const [open, setOpen] = useState(false);
  const wrapRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    if (!open) return;
    const onPointerDown = (e: MouseEvent | TouchEvent) => {
      if (!wrapRef.current?.contains(e.target as Node)) setOpen(false);
    };
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === "Escape") setOpen(false);
    };
    document.addEventListener("mousedown", onPointerDown);
    document.addEventListener("keydown", onKeyDown);
    return () => {
      document.removeEventListener("mousedown", onPointerDown);
      document.removeEventListener("keydown", onKeyDown);
    };
  }, [open]);

  return (
    <div ref={wrapRef} className="relative">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        aria-haspopup="dialog"
        aria-expanded={open}
        aria-label="ارسال"
        style={{ backgroundColor: skin.accent, color: skin.onAccent }}
        className="flex h-8 w-8 items-center justify-center rounded-full transition-colors duration-500"
      >
        <ArrowUp className="h-4 w-4" aria-hidden />
      </button>

      {open && (
        <div
          role="dialog"
          aria-label="ورود یا ثبت‌نام"
          className="lv-pop absolute bottom-[calc(100%+0.75rem)] end-0 z-20 w-[min(278px,calc(100vw-3rem))] rounded-lg border border-black/[0.08] bg-white p-4 text-start shadow-[0_12px_32px_-12px_rgb(66_40_14_/_0.35)]"
        >
          <p className="text-[14px] font-bold leading-[1.8] text-ink-950">
            برای دیپلوی وارد شوید
          </p>
          <p className="mt-1 text-[12.5px] leading-[1.9] text-ink-500">
            حساب بسازید تا پروژه‌تان را همین حالا آنلاین کنیم.
          </p>
          <div className="mt-4 grid grid-cols-2 gap-2">
            <a
              href={DASHBOARD_SIGNUP_URL}
              className="inline-flex h-9 items-center justify-center rounded-md bg-ink-950 px-3 text-[12.5px] font-semibold text-white transition-colors hover:bg-ink-800"
            >
              ثبت‌نام رایگان
            </a>
            <a
              href={DASHBOARD_SIGNIN_URL}
              className="inline-flex h-9 items-center justify-center rounded-md border border-black/[0.12] bg-white px-3 text-[12.5px] font-semibold text-ink-800 transition-colors hover:border-black/25 hover:text-ink-950"
            >
              ورود
            </a>
          </div>
          <span
            className="absolute -bottom-[7px] end-2.5 h-3 w-3 rotate-45 border-b border-e border-black/[0.08] bg-white"
            aria-hidden
          />
        </div>
      )}
    </div>
  );
}

export function PromptCard() {
  const { text, skin } = useTypedPrompt();
  return <PromptComposer text={text} skin={skin} />;
}

/**
 * The composer card itself, driven from outside: the home hero runs its own
 * timeline (type → send → the canvas builds) and feeds the text in. `note`
 * replaces the caret with a status line once the prompt has been "sent".
 */
export function PromptComposer({
  text,
  skin,
  note,
  project = "پروژه‌ی سایت شرکت",
}: {
  text: string;
  skin: AgentSkin;
  note?: React.ReactNode;
  project?: string;
}) {
  return (
    <div
      style={{ backgroundColor: skin.shell, borderColor: skin.border }}
      className="lv-card mx-auto max-w-[680px] rounded-[1.6rem] p-1.5 shadow-[0_2px_4px_rgb(66_40_14_/_0.05),0_30px_60px_-24px_rgb(196_90_12_/_0.25)] transition-colors duration-500"
    >
      <div
        style={{ backgroundColor: skin.surface }}
        className="rounded-[1.15rem] p-5 pb-2.5 text-start transition-colors duration-500 sm:p-6 sm:pb-2.5"
      >
        {/* Whose composer this is right now. */}
        <div className="flex items-center gap-2 pb-3.5">
          <img
            src={skin.src}
            alt=""
            className={`h-5 w-5 rounded-[5px] object-contain ${"invertMark" in skin && skin.invertMark ? "invert" : ""}`}
          />
          <span
            dir="ltr"
            style={{ color: skin.muted }}
            className="text-[12.5px] font-semibold transition-colors duration-500"
          >
            {skin.label}
          </span>
        </div>

        <p
          style={{ color: skin.text }}
          className="min-h-[3.8rem] text-[15px] leading-[1.9] transition-colors duration-500 sm:min-h-[3.25rem] sm:text-[17px]"
        >
          {text}
          {!note && (
            <span
              style={{ backgroundColor: skin.accent }}
              className="lv-caret mr-0.5 inline-block h-[1.05em] w-[2px] translate-y-[0.18em]"
            />
          )}
        </p>
        {note && (
          <div style={{ color: skin.muted }} className="mt-1 text-[12.5px] font-medium transition-colors duration-500">
            {note}
          </div>
        )}

        <div className="mt-5 flex items-center justify-between gap-3">
          <div className="flex items-center gap-2">
            <span
              style={{ color: skin.muted, boxShadow: `inset 0 0 0 1px ${skin.chipRing}` }}
              className="inline-flex items-center gap-1.5 rounded-full px-3 py-1.5 text-[12px] font-semibold transition-colors duration-500"
            >
              <GitBranch className="h-3.5 w-3.5" style={{ color: skin.accent }} aria-hidden />
              {project}
            </span>
          </div>
          <SendWithAuthPopover skin={skin} />
        </div>
      </div>
    </div>
  );
}
